import React from 'react'
import PopOver from '@/components/UiElements/PopOver'
import ImageComponent from '@/components/UiElements/ImageComponent'
import createFileData from '@/components/Form/utils/file/createFileData'
import { FilePreview } from '@/components/Form/types'
import { DEFAULT_MATERIAL_COLOR, normalizeHexInput } from '../utils/materialColor'

export type MaterialSourceMode = 'color' | 'pattern'

export interface MaterialSource {
	mode: MaterialSourceMode
	color: string
	file: FilePreview | null
}

export const defaultMaterialSource = (): MaterialSource => ({
	mode: 'color',
	color: DEFAULT_MATERIAL_COLOR,
	file: null,
})

const ACCEPTED_FORMATS = 'image/*, .avif, .webp, .png, .jpg, .jpeg'

interface MaterialPickerProps {
	formData: any
	onChange?: (name: string, value: any) => void
}

/**
 * Compact editor for a custom material, shown in a popover.
 *
 * A custom material is a colour OR a pattern, never both — `mode` is what makes
 * that structural rather than a rule someone has to remember. Only the active
 * mode's value is written to target_object on save, so a colour picked and then
 * abandoned for a pattern never reaches the JSON.
 *
 * Both values are kept in form state while the section is open, so flipping
 * between the two to compare does not throw away what you already set.
 */
const MaterialPicker: React.FC<MaterialPickerProps> = ({ formData, onChange }) => {
	const value: MaterialSource = formData?.materialSource || defaultMaterialSource()
	const [hexDraft, setHexDraft] = React.useState<string>(value.color)

	React.useEffect(() => {
		setHexDraft(value.color)
	}, [value.color])

	const update = (patch: Partial<MaterialSource>) => {
		onChange?.('materialSource', { ...value, ...patch })
	}

	const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return
		const fileData = await createFileData({ file, types: ACCEPTED_FORMATS })
		if (fileData) update({ file: fileData })
		// Let the same file be picked again after a remove.
		event.target.value = ''
	}

	const isPattern = value.mode === 'pattern'
	const patternSrc = value.file?.string || value.file?.url || null

	const tabClass = (active: boolean) => `
		flex-1 h-[32px] rounded-md text-text-sm transition-all
		${active
			? 'bg-primary-500 text-white shadow-xs'
			: 'bg-transparent text-gray-600 hover:bg-gray-100'}
	`

	const summary = isPattern
		? (value.file?.name || 'No pattern chosen')
		: value.color

	return (
		<PopOver
			placement="bottom left"
			toggle={() => (
				<button
					type="button"
					className="flex w-full items-center gap-sm rounded-md border border-gray-300 bg-white px-md shadow-xs
						h-[44px] text-text-md text-gray-900 hover:border-primary-500 transition-all"
				>
					{isPattern
						? (patternSrc
							? <ImageComponent
								src={patternSrc}
								style={{ height: '24px', width: '24px' }}
								className="w-[24px] h-[24px] rounded-sm shrink-0"
								id={value.file?.uuid || 'material-pattern'}
							/>
							: <span className="h-[24px] w-[24px] shrink-0 rounded-sm border border-dashed border-gray-300" />)
						: <span
							className="h-[24px] w-[24px] shrink-0 rounded-sm border border-gray-300"
							style={{ backgroundColor: value.color }}
						/>}
					<span className="truncate">{summary}</span>
					<span className="ml-auto text-gray-400 text-text-sm">{isPattern ? 'Pattern' : 'Colour'}</span>
				</button>
			)}
			content={() => (
				<div className="w-[280px] p-sm flex flex-col gap-sm">
					<div className="flex gap-xs rounded-md bg-gray-50 p-[2px]">
						<button type="button" className={tabClass(!isPattern)} onClick={() => update({ mode: 'color' })}>
							Colour
						</button>
						<button type="button" className={tabClass(isPattern)} onClick={() => update({ mode: 'pattern' })}>
							Pattern
						</button>
					</div>

					{isPattern ? (
						<>
							{patternSrc && (
								<div className="flex items-center gap-sm">
									<ImageComponent
										src={patternSrc}
										style={{ height: '48px', width: '48px' }}
										className="w-[48px] h-[48px] rounded-md"
										id={`${value.file?.uuid || 'material'}-preview`}
									/>
									<span className="truncate text-text-sm text-gray-600">{value.file?.name}</span>
									<button
										type="button"
										className="ml-auto text-text-sm text-red-500 hover:underline"
										onClick={() => update({ file: null })}
									>
										Remove
									</button>
								</div>
							)}
							<label
								className="flex h-[44px] cursor-pointer items-center justify-center rounded-md border border-dashed
									border-gray-300 text-text-sm text-gray-600 hover:border-primary-500 hover:text-primary-500 transition-all"
							>
								{patternSrc ? 'Replace image' : 'Choose image'}
								<input type="file" accept={ACCEPTED_FORMATS} className="hidden" onChange={handleFile} />
							</label>
							<p className="text-text-xs text-gray-400">Resized to a 512x512 tile on upload.</p>
						</>
					) : (
						<>
							<div className="flex items-center gap-sm">
								<input
									type="color"
									aria-label="Material colour"
									className="h-[44px] w-[56px] shrink-0 cursor-pointer rounded-md border border-gray-300 bg-white p-[4px]"
									value={normalizeHexInput(value.color) || DEFAULT_MATERIAL_COLOR}
									onChange={(e) => update({ color: e.target.value })}
								/>
								<input
									type="text"
									spellCheck={false}
									placeholder={DEFAULT_MATERIAL_COLOR}
									className="h-[44px] w-full rounded-md border border-gray-300 px-md text-text-md outline-none
										hover:border-primary-500 focus:border-primary-500 focus:shadow-focus transition-all"
									value={hexDraft}
									onChange={(e) => {
										setHexDraft(e.target.value)
										const hex = normalizeHexInput(e.target.value)
										if (hex) update({ color: hex })
									}}
									onBlur={() => setHexDraft(normalizeHexInput(hexDraft) || value.color)}
								/>
							</div>
							<p className="text-text-xs text-gray-400">
								Engraving Tint controls brightness; this sets the hue.
							</p>
						</>
					)}
				</div>
			)}
		/>
	)
}

export default MaterialPicker
